'use client';

import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import { Button, IcArrowDown, IcLikeFilled, Image, LikeMotion, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import type { LikedSnapsCountMap, MyCurationSnapItem } from '@musinsa/services-curator';
import { ERROR_CODE_LIKE_UNAUTHORIZED, isApiError } from '@musinsa/services-curator';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { getPaths } from '../../common';
import { createLoginUrl } from '../../common/auth/redirectToLogin';
import { useLikedSnapsCountQuery } from '../../common/hooks/useLikedSnapsCountQuery';
import { useNavigate } from '../../common/hooks/useNavigate';
import { useSnapLikeMutation } from '../../common/hooks/useSnapLikeMutation';
import { curatorQueries } from '../../query-factory';

interface Props {
  title: string;
  curationId: string;
  items: MyCurationSnapItem[];
  pageCache: Map<string, number>;
  setPageCache: React.Dispatch<React.SetStateAction<Map<string, number>>>;
  curationIndex: number;
  defaultPage?: number;
}

const MIN_COUNT = 9;

interface SnapTileProps {
  curationId: string;
  snap: MyCurationSnapItem;
  index: number;
  curatorSnsTitle: string;
  isLiked: boolean;
}

const MyShopSnapTile = ({ curationId, snap, index, curatorSnsTitle, isLiked }: SnapTileProps) => {
  const { navigate } = useNavigate();
  const { isMobile, service } = useDeviceInfo();
  const isMusinsa = service === SERVICE.MUSINSA;
  const paths = getPaths(service);

  const snapLikeMutation = useSnapLikeMutation({
    onError: (error) => {
      if (isApiError(error) && error.data?.meta?.errorCode === ERROR_CODE_LIKE_UNAUTHORIZED) {
        const url = createLoginUrl(window.location.href);
        navigate(url);
      }
    },
  });

  const onClickSnap = useCallback(() => {
    if (isMusinsa && curatorSnsTitle) {
      navigate(
        paths.snapDetail({
          curationId,
          query: { snapId: snap.id, shopSlug: curatorSnsTitle },
        }),
        { newWebview: true },
      );
    } else {
      navigate(snap.linkUrl, { newWebview: true });
    }
    if (isMusinsa && curatorSnsTitle) {
      amplitudeSendEvent({
        action_name: 'click',
        component_name: 'snap',
        event_name: 'click_snap',
        from: getScreenName(),
        id: curatorSnsTitle,
        index,
        name: snap.id,
        section_name: 'curator_curationfeed',
        type: 'snap',
      });
    }
  }, [curationId, curatorSnsTitle, index, isMusinsa, navigate, paths, snap.id, snap.linkUrl]);

  const handleClickLike = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    const next = !isLiked;
    snapLikeMutation.mutate({ snapId: snap.id, isLiked: next });

    if (isMusinsa && curatorSnsTitle) {
      amplitudeSendEvent({
        action_name: next ? 'like_snap' : 'unlike_snap',
        component_name: 'snap',
        event_name: next ? 'like_snap_snap' : 'unlike_snap_snap',
        from: getScreenName(),
        id: curatorSnsTitle,
        index,
        name: snap.id,
        section_name: 'curator_curationfeed',
        type: 'snap',
      });
    }
  };

  return (
    <div className="relative w-full h-full overflow-hidden bg-neutral-color-white">
      <div
        role="button"
        tabIndex={0}
        className="relative w-full cursor-pointer"
        style={{ aspectRatio: '5/6', fontSize: 0 }}
        onClick={onClickSnap}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClickSnap();
          }
        }}
      >
        <Image
          src={`${snap.imageUrl}?width=${isMobile ? 250 : 400}&q=75&format=webp`}
          alt=""
          className="block size-full object-cover"
          loading={index === 0 ? 'eager' : 'lazy'}
          width={5}
          height={6}
        />
      </div>
      <div className="absolute right-1 bottom-1 z-10 inline-flex">
        <LikeMotion like={isLiked}>
          {isLiked ? (
            <IcLikeFilled
              className="cursor-pointer"
              fillColor="red"
              strokeColor="transparent"
              width="20px"
              height="20px"
              onClick={handleClickLike}
            />
          ) : (
            <IcLikeFilled
              className="cursor-pointer"
              fillColor="gray-500"
              strokeColor="white"
              opacity={0.6}
              width="20px"
              height="20px"
              onClick={handleClickLike}
            />
          )}
        </LikeMotion>
      </div>
    </div>
  );
};

export const MyShopSnapItems = ({
  title: _title,
  curationId,
  curationIndex: _curationIndex,
  items,
  pageCache,
  setPageCache,
  defaultPage = 1,
}: Props) => {
  const { service } = useDeviceInfo();
  const isMusinsa = service === SERVICE.MUSINSA;
  const currentPage = pageCache.get(curationId) || defaultPage;

  const { data: userInfo } = useQuery({
    ...curatorQueries.profile.userInfo,
  });
  const curatorSnsTitle = userInfo?.snsTitle ?? '';

  const snapIds = useMemo(() => items.map((item) => item.id), [items]);
  const { data: likedMap } = useLikedSnapsCountQuery(snapIds, { enabled: isMusinsa });
  const likedSnapsMap: LikedSnapsCountMap = likedMap ?? {};

  const showMore = useMemo(
    () => items.length > MIN_COUNT * currentPage,
    [items.length, currentPage],
  );

  const onLoadMore = useCallback(() => {
    if (isMusinsa && curatorSnsTitle) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'curation_more',
        component_name: 'more_button',
        event_name: 'click_more_button',
        from: getScreenName(),
        id: curatorSnsTitle,
        section_name: 'curator_curationfeed',
        type: 'snap',
      });
    }
    const nextPage = currentPage + 1;
    setPageCache((prev) => new Map(prev).set(curationId, nextPage));
  }, [curationId, currentPage, curatorSnsTitle, isMusinsa, setPageCache]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="grid w-full grid-cols-3 bg-neutral-color-white">
        {[...items].slice(0, MIN_COUNT * currentPage).map((snap, index) => (
          <MyShopSnapTile
            key={snap.id}
            curationId={curationId}
            curatorSnsTitle={curatorSnsTitle}
            index={index}
            snap={snap}
            isLiked={likedSnapsMap[snap.id]?.isLiked ?? false}
          />
        ))}
      </div>
      {showMore && (
        <div className="flex justify-center px-4 py-3">
          <Button
            className="flex w-full items-center gap-[2px]"
            size={40}
            variant="tertiaryOutlinedGray"
            onClick={onLoadMore}
          >
            <Typography variant="body14pxMed">
              더보기({MIN_COUNT * currentPage}/{items.length}개)
            </Typography>
            <IcArrowDown height="16px" width="16px" />
          </Button>
        </div>
      )}
    </div>
  );
};
